import { reactivityBatches, reactivityRunsEffects, } from "./reactivity-contract.js";
import { isRecord } from "./record-utils.js";
import { destroyNestedUnder, nestedAt, registerRowNode, rowDeclaresCell } from "./collections/register.js";
import { MDY_DEV } from "./dev-flags.js";
/** The place in a reordering that no row came from: the row the change created. */
const NEW_ROW = -1;
/**
 * Owns one array node: registers/removes row fields on the engine so the
 * structure follows the value, and implements push/insert/remove/move/setAll.
 */
/**
 * Refuses, when the form is built, a shape this manager cannot run.
 *
 * A row may hold fields, groups and collections of either kind, at any depth: a collection inside a
 * row is addressed by the pattern its declaration has — `items.*`, `orders.*.lines` — so a second
 * positional level names its rows as unambiguously as the first. What is still walked is the *kind*
 * of every node, so a shape that is not a form at all fails where the schema was written rather than
 * at the first `push` in front of a user.
 */
function assertRowShape(node) {
    // Over a stack: the depth this walks is the schema's own, and a schema built from a document has
    // no cap — a recursive check would refuse a deep form by exhausting the stack, which is not the
    // refusal this function exists to give.
    const pending = [node];
    while (pending.length > 0) {
        const current = pending.pop();
        if (current.kind === "array" || current.kind === "record") {
            pending.push(current.item);
            continue;
        }
        if (current.kind === "group") {
            for (const child of Object.values(current.children))
                pending.push(child);
            continue;
        }
        if (current.kind !== "field") {
            throw new Error(`[modyra] A row may hold a field, a group or a collection — not ${String(current.kind)}`);
        }
    }
}
export class MdyArrayManager {
    collectionKind = "array";
    _deps;
    _initial;
    _rowCountSig;
    _reconcile;
    _releaseGate;
    /** Track the last known indices for cleanup detection. */
    _lastPresentIndices = new Set();
    /** Current number of registered rows. */
    rowCount;
    constructor(deps, initial) {
        // Checked here rather than when a row first arrives: a schema that cannot work should fail where
        // it was written, not on the first `push` in front of a user.
        assertRowShape(deps.item);
        this._deps = deps;
        this._initial = initial;
        this._rowCountSig = deps.rx.signal(0);
        this.rowCount = this._rowCountSig.asReadonly();
        // Registers a phantom field at the array's own path so array-level
        // validator errors (routed to this path) surface via engine.errorsFor()
        // and state.valid — errorsFor() only merges cross-errors for paths with
        // a real field record. Its own value is never read or written.
        deps.engine.getField(deps.path);
        this._rebuild(initial.slice());
        this._releaseGate = deps.engine.registerPathGate(deps.path, {
            /**
             * Which rows are in play: the ones this collection has.
             *
             * Its rows still follow its *value* — a write below this path is a row of it, which is how a
             * restored draft brings one back, and `onRefusedWrite` below grows the list to receive it. A
             * *claim* is not a write: a control binding to a row the list does not have waits for it, as
             * it does in a keyed collection, instead of bringing a row into being. Without that
             * distinction, rendering decided what existed — a control bound to row 1 of an empty list made
             * two rows, one of them a hole `getValue()` could not describe.
             */
            isOpen: (name) => {
                // The collection's own path is not a row of it: the phantom field there carries the
                // collection-level errors, and a gate that refused it would take the collection out of its
                // own form's value.
                if (name === this._deps.path)
                    return true;
                const index = this._indexUnder(name);
                return index !== null && index < this._deps.rx.untracked(() => this._rowCountSig());
            },
            /**
             * A value arriving for a row the list does not have yet is the owner's own data — a restored
             * draft, an undo across the moment a row was pushed — so the list grows to receive it rather
             * than dropping it.
             */
            onRefusedWrite: (name) => {
                const index = this._indexUnder(name);
                if (index === null)
                    return;
                // The path is the instruction, and an extra segment is one too: `lines.0.b.sku` asks for a
                // member of a row no template describes. Grown to receive it, the list holds a row whose
                // shape its own document never declared, and nothing is there to be invalid about it.
                const rest = name.slice(`${this._deps.path}.${index}`.length + 1);
                if (!rowDeclaresCell(this._deps.item, rest)) {
                    if (MDY_DEV) {
                        console.warn(`[modyra] Ignored "${name}": a row of "${this._deps.path}" declares no member at ` +
                            `"${rest}", so the path describes a row this collection cannot hold.`);
                    }
                    return;
                }
                const count = this._deps.rx.untracked(() => this._rowCountSig());
                if (index < count)
                    return;
                // One row at a time, and only the next one. An index past the end names a row *and every row
                // between*, none of which the write carries a value for: `tags.5` on a list of one made five
                // entries nobody typed, and the number came from storage, which anything on the origin can
                // write. A large enough number stopped the form opening at all.
                //
                // A write that legitimately carries a list carries every index in it, and `patchValue`
                // applies a flat write in path order — so the rows arrive one after another and each is the
                // next.
                if (index > count) {
                    if (MDY_DEV) {
                        console.warn(`[modyra] Ignored "${name}": "${this._deps.path}" has ${count} row(s), and this path ` +
                            `names row ${index} — a list grows to receive the next row, not to reach a number.`);
                    }
                    return;
                }
                // The count first: the rows are in play before their fields are written, or the gate this
                // very callback exists to open would refuse the registration it is making.
                this._rowCountSig.set(index + 1);
                this._registerNode(`${this._deps.path}.${index}`, this._deps.item, undefined, `${this._deps.path}.${index}`, this._deps.sections ?? []);
            },
            // A whole-value write is a statement of which rows there are. Read as such it is the only
            // thing that can say a row *ceased* to exist: the engine writes flat paths and sets an absent
            // field to null rather than removing it, so a name that stays is a row that stays — which is
            // how undoing a push left an empty row behind, and how a draft written after a deletion
            // brought the deleted row back.
            onReplace: (paths) => this._keepOnly(this._presentIndices([...paths])),
        });
        this._reconcile = reactivityRunsEffects(deps.rx)
            ? deps.rx.effect(() => {
                const present = this._declaredIndices();
                deps.rx.untracked(() => this._absorb(present));
            })
            : null;
    }
    /**
     * Drops every row a whole-value write did not carry.
     *
     * Only drops. A write that carries *more* rows than are declared is growth, and growth is where
     * rows get their validators — registering them here instead would leave the reconciliation with
     * nothing left to do and the new rows unvalidated. So the count falls to what survives and is
     * never raised.
     *
     * The surviving rows keep their positions: an array is contiguous, and a write that skips an index
     * leaves that gap empty rather than closing it and moving someone else's data.
     */
    _keepOnly(present) {
        const count = this._rowCountSig();
        let highest = -1;
        for (const index of present)
            if (index > highest)
                highest = index;
        for (let index = 0; index < count; index += 1) {
            if (!present.has(index))
                this._removeRow(index);
        }
        const kept = Math.min(count, highest + 1);
        this._rowCountSig.set(kept);
        if (kept < count)
            this._deps.engine.refreshPathGate(this._deps.path);
    }
    push(value) {
        const values = this._currentValues();
        values.push(value);
        // The new row is the only one that is new; nothing above it moved.
        this._rebuild(values, values.length - 1);
    }
    /**
     * Whether `index` names a position at all.
     *
     * A position is computed — from a route parameter, a `data-` attribute, a lookup — and every one
     * of those has a way of coming out wrong that produces no number: `Number.parseInt` of a missing
     * attribute is `NaN`, a lookup that missed is `undefined`. Compared against the list's bounds,
     * each of them answered "in range" and `splice` then read it as 0, so the one mistake that
     * produces no number deleted the first row instead of nothing. The engine's own answer for a
     * number that is not a position — `-1`, `99`, `Infinity` — is to leave the collection alone, and
     * this is that answer applied to the values that are not numbers.
     */
    /**
     * `index` as a position, or null when it names none.
     *
     * A digit string counts: a position arrives from a `data-` attribute, a route parameter or a form
     * control, and every one of those hands over text. What does not count is text that is not a
     * number, or a number that is not a whole one — those are the mistakes that produced no position
     * at all and were read as zero.
     */
    static _positionOf(index) {
        if (typeof index === "number")
            return Number.isInteger(index) ? index : null;
        if (typeof index !== "string" || !/^-?\d+$/.test(index))
            return null;
        const parsed = Number(index);
        return Number.isInteger(parsed) ? parsed : null;
    }
    insert(index, value) {
        const position = MdyArrayManager._positionOf(index);
        if (position === null)
            return;
        const values = this._currentValues();
        const at = Math.max(0, Math.min(values.length, position));
        const order = this._identityOrder(values.length);
        order.splice(at, 0, NEW_ROW);
        values.splice(at, 0, value);
        this._carryBindings(order);
        this._rebuild(values, at);
    }
    remove(index) {
        const values = this._currentValues();
        // An index the list does not have is not a removal, and a change that changes nothing must not
        // reset what the user did.
        const position = MdyArrayManager._positionOf(index);
        if (position === null || position < 0 || position >= values.length)
            return;
        const order = this._identityOrder(values.length);
        order.splice(position, 1);
        values.splice(position, 1);
        this._carryBindings(order, [position]);
        this._rebuild(values, position);
    }
    move(from, to) {
        const start = MdyArrayManager._positionOf(from);
        const target = MdyArrayManager._positionOf(to);
        if (start === null || target === null)
            return;
        const values = this._currentValues();
        const removed = values.splice(start, 1);
        if (removed.length === 0)
            return;
        const at = Math.max(0, Math.min(values.length, target));
        values.splice(at, 0, removed[0]);
        const order = this._identityOrder(values.length + 1);
        const [moved] = order.splice(start, 1);
        order.splice(at, 0, moved);
        this._carryBindings(order);
        this._rebuild(values, Math.min(start, at));
    }
    /** `[0, 1, … length - 1]`, the order before a structural change rearranges it. */
    _identityOrder(length) {
        return Array.from({ length }, (_, index) => index);
    }
    /**
     * Carries what a binder said about each row to the index that row now has.
     *
     * `order[newIndex]` is the index the row had before the change, or {@link NEW_ROW} for one the
     * change created. A binding is the consumer's word about a *row* — this cell is not for editing —
     * so leaving it at the index while the rows move under it would suppress a cell of whichever row
     * arrived there. `ended` names the rows the change removed, whose bindings go with them.
     */
    _carryBindings(order, ended = []) {
        const suffixes = this._leafPaths(`${this._deps.path}.0`, this._deps.item)
            .map((path) => path.slice(`${this._deps.path}.0`.length));
        if (suffixes.length === 0)
            return;
        const pathAt = (index, suffix) => `${this._deps.path}.${index}${suffix}`;
        for (const index of ended) {
            for (const suffix of suffixes)
                this._deps.engine.clearBindings(pathAt(index, suffix));
        }
        const pairs = [];
        for (const [newIndex, oldIndex] of order.entries()) {
            if (oldIndex === NEW_ROW || oldIndex === newIndex)
                continue;
            for (const suffix of suffixes)
                pairs.push([pathAt(oldIndex, suffix), pathAt(newIndex, suffix)]);
        }
        if (pairs.length > 0)
            this._deps.engine.carryBindings(pairs);
    }
    setAll(values) {
        this._rebuild(values.slice());
    }
    /** Current row values, read back from the engine. */
    getValues() {
        return this._currentValues();
    }
    /** Every leaf path of every current row — what a parent collection treats as this array's fields. */
    leafPathsNow() {
        return Array.from({ length: this.rowCount() }, (_, index) => this._leafPaths(`${this._deps.path}.${index}`, this._deps.item)).flat();
    }
    /** Rebuilds the rows back to the schema's declared initial array. */
    resetToInitial() {
        this.setAll(this._initial);
    }
    /** Releases the reconciliation effect — call when the owning form is destroyed. */
    destroy() {
        this._releaseGate();
        this._reconcile?.destroy();
        for (const manager of this._nested.values())
            manager.destroy();
        this._nested.clear();
    }
    // ── Internals ────────────────────────────────────────────────────────────
    _currentValues() {
        const count = this._rowCountSig();
        const out = [];
        for (let i = 0; i < count; i++) {
            out.push(this._readNode(`${this._deps.path}.${i}`, this._deps.item));
        }
        return out;
    }
    /**
     * Writes the rows the collection now has.
     *
     * Only the rows past the new end are removed. The ones that remain are registered again in place,
     * because a push at one end is not the other rows ending: a control bound to one of them never
     * moved, and tearing its field down would release the claim it holds and drop what it said about
     * the cell — a disabled column would come back enabled and be submitted.
     *
     * What a structural change *does* end is the state the user's interaction produced. Touched and
     * dirty do not travel with a row across an insert, a move or a whole-value write, so the rows that
     * survive are marked clean here rather than by being destroyed.
     */
    _rebuild(values, movedFrom = 0) {
        // The whole structural change is one change to observe, ending rows included. Registration alone
        // was batched, which left the other half exposed: a runtime whose computations run eagerly reads
        // the form between a row's cells ending and finds a shape the schema does not describe, so a
        // `remove` or a `setAll` raised where a `push` had been made safe.
        this._batched(() => this._rebuildNow(values, movedFrom));
    }
    _rebuildNow(values, movedFrom) {
        const prevCount = this._rowCountSig();
        // What the list holds now, as plain data: the state to go back to if writing the new rows raises.
        const before = this._currentValues();
        for (let i = values.length; i < prevCount; i++)
            this._removeRow(i);
        // Only from the first row the change actually moved. Appending a line moves nothing above it,
        // and a user's marks are theirs: clearing them all would make the errors a form only shows on a
        // visited field vanish when a row is added at the other end.
        for (let i = movedFrom; i < Math.min(prevCount, values.length); i++)
            this._clearRowInteraction(i);
        // The rows are in play before their fields are written: the gate answers from this count, and a
        // registration is the collection declaring a row rather than something asking for one. The
        // refresh in between is what takes the fields of the rows that just ended.
        this._rowCountSig.set(values.length);
        if (values.length < prevCount)
            this._deps.engine.refreshPathGate(this._deps.path);
        try {
            values.forEach((v, i) => this._registerNode(`${this._deps.path}.${i}`, this._deps.item, v, `${this._deps.path}.${i}`, this._deps.sections ?? []));
        }
        catch (error) {
            // Reading a row's value can raise — a getter over a store that is not loaded, a proxy that
            // refuses. The list goes back to the rows it had, because a count that says one thing while
            // the value says another is a list a consumer cannot iterate. `before` was read out of the
            // engine, so restoring it cannot raise in turn.
            this._rowCountSig.set(before.length);
            this._deps.engine.refreshPathGate(this._deps.path);
            before.forEach((v, i) => this._registerNode(`${this._deps.path}.${i}`, this._deps.item, v, `${this._deps.path}.${i}`, this._deps.sections ?? []));
            throw error;
        }
        // Update tracking after rebuild (structural ops are always atomic)
        this._lastPresentIndices = new Set(Array.from({ length: values.length }, (_, i) => i));
    }
    /** Runs `write` as one change where the runtime can, and plainly where it cannot. */
    _batched(write) {
        const rx = this._deps.rx;
        if (reactivityBatches(rx))
            rx.batch(write);
        else
            write();
    }
    /** Forgets what the user did to a row that a structural change is rewriting. */
    _clearRowInteraction(index) {
        for (const path of this._leafPaths(`${this._deps.path}.${index}`, this._deps.item)) {
            const ref = this._deps.engine.peekField(path);
            if (!ref)
                continue;
            const state = ref();
            state.touched.set(false);
            state.dirty.set(false);
        }
    }
    /** The row a path belongs to, as the value an enclosing condition reads. */
    _rowValue(rowPath) {
        const row = this._readNode(rowPath, this._deps.item);
        return isRecord(row) ? row : {};
    }
    _registerNode(fullPath, rowNode, value, rowPath, sections = []) {
        registerRowNode(this._registration, fullPath, rowNode, value, rowPath, sections);
    }
    /** What the shared visit needs from this manager, built once. */
    get _registration() {
        return {
            engine: this._deps.engine,
            rx: this._deps.rx,
            readRow: (rowPath) => this._readNode(rowPath, this._deps.item),
            readNode: (path, node) => this._readNode(path, node),
            rowValue: (rowPath) => this._rowValue(rowPath),
            // A clause written in a row's template cannot name the row's key, so what encloses it is the
            // row; this is the way back out to the form, which is what `{ root: true }` reads.
            readRoot: this._deps.readRoot,
            onCollection: (path, node, value) => {
                this._declareNested(path, node, value);
            },
        };
    }
    /** The record managers rows declared, keyed by their full path. */
    _nested = new Map();
    _declareNested(path, node, value) {
        // The subtree being replaced ends with the declaration that held it: its manager goes, and so do
        // the fields it registered. Left behind, they are read back as rows by the reconciliation and the
        // old list reappears under the new one.
        const replaced = this._nested.get(path);
        if (replaced) {
            const leaves = replaced.leafPathsNow();
            const collections = replaced.collectionPathsNow();
            replaced.destroy();
            for (const leaf of leaves)
                this._deps.engine.endField(leaf);
            // And the field each collection below registered under its own path. It is not a leaf, so the
            // line above does not reach it, and one left behind is a row the reconciliation declares
            // again — the row comes back empty under the list that replaced it.
            for (const nested of collections)
                this._deps.engine.endField(nested);
        }
        this._nested.set(path, this._deps.createCollection(node.kind, {
            rx: this._deps.rx,
            engine: this._deps.engine,
            path,
            item: node.item,
            // Everything below an array is under a positional level; the flag says so for the readers
            // that order a rebuild by it, and refuses nothing.
            positionalAncestor: true,
            // Nothing here has a name to complain about that the engine has not already reported, so a
            // nested record's diagnostics go where the engine's do.
            // Forwarded, not swallowed. Discarded here, a collection declared inside a row lost every
            // diagnostic it tried to emit — the same mistake reported at the top level and inside a
            // keyed row said nothing at all inside a positional one.
            warn: this._deps.warn,
            // A row's collection is out of play with the row: an array's rows are positional, so what
            // says the row exists is the count, not a declaration.
            sections: [
                ...(this._deps.sections ?? []),
                () => Number(path.slice(this._deps.path.length + 1).split(".")[0]) < this._rowCountSig(),
            ],
            createCollection: this._deps.createCollection,
        }, value));
    }
    /** Every collection path below this one — see {@link MdyNestedCollection.collectionPathsNow}. */
    collectionPathsNow() {
        return [...this._nested].flatMap(([path, manager]) => [path, ...manager.collectionPathsNow()]);
    }
    /** Everything a row owned, the collections it declared included. */
    _destroyNestedUnder(prefix) {
        destroyNestedUnder(this._nested, this._deps.engine, prefix);
    }
    /** The manager for a collection a row declared below this one, if it is still alive. */
    nested(path) {
        return nestedAt(this._nested, path);
    }
    /**
     * Replaces the rows wholesale, from whatever the enclosing write carried.
     *
     * A value that is not an array says nothing about rows, so it changes nothing.
     */
    setAllFrom(value) {
        if (Array.isArray(value))
            this.setAll(value);
    }
    /**
     * The rows a patch names, over the rows the collection has.
     *
     * The list is still the whole list — its length says which rows there are, because an index is a
     * row's identity — but a row it carries is read the way a keyed collection reads one: the cells it
     * names are written, and the cells it does not name stay as they are. Rebuilding from the row
     * alone gave an unnamed cell the *declaration's* initial, which is neither what the person typed
     * nor what the row started as: it is what a row created from nothing gets. A change set that
     * leaves out a disabled cell would put that value back on the way in.
     *
     * A row past the end is new, so there is nothing to keep and it is taken as it came.
     */
    patchFrom(value) {
        if (!Array.isArray(value))
            return;
        const held = this._currentValues();
        this.setAll(value.map((row, index) => (index < held.length ? this._merged(this._deps.item, held[index], row) : row)));
    }
    /**
     * A row's incoming value over its held one, by the shape the schema declares.
     *
     * Driven by the descriptor and not by the value: an object-valued *leaf* — a range, a colour — is
     * one value and is replaced, where a group of cells is merged cell by cell. A collection inside a
     * row is written whole by its own manager, as a whole-row write already does.
     */
    _merged(node, held, incoming) {
        // A collection inside a row is merged here rather than left to its own manager, and the reason
        // is the order things happen in: writing this row goes through `setAll`, which rebuilds the
        // subtree — so by the time the inner manager is asked to merge, it has no rows left to merge
        // against and every cell the patch did not name comes back as its declared initial. The value
        // handed to `setAll` has to be complete already.
        if (node.kind === "array") {
            if (!Array.isArray(incoming) || !Array.isArray(held))
                return incoming;
            const item = node.item;
            // The incoming list says which rows there are; each of them is merged over the row that was
            // at its index, and a row past the end is new and taken as it came.
            return incoming.map((row, index) => (index < held.length ? this._merged(item, held[index], row) : row));
        }
        if (node.kind === "record") {
            if (!isRecord(incoming) || !isRecord(held))
                return incoming;
            const item = node.item;
            const out = {};
            for (const [key, row] of Object.entries(incoming)) {
                out[key] = key in held ? this._merged(item, held[key], row) : row;
            }
            return out;
        }
        if (node.kind !== "group")
            return incoming;
        if (!isRecord(incoming) || !isRecord(held))
            return incoming;
        const out = { ...held };
        for (const [key, child] of Object.entries(node.children)) {
            if (!(key in incoming))
                continue;
            out[key] = this._merged(child, held[key], incoming[key]);
        }
        return out;
    }
    /**
     * Ends a row: its subtree, its ownership, and then its fields.
     *
     * The fields go when the gate stops admitting them — the count falls and
     * {@link MdyFormEngine.refreshPathGate} destroys what the collection no longer has, putting the
     * claims of controls still bound there back into waiting. Calling `removeField` here instead
     * would ask the engine to release a *control's* claim, which is not what a row ending means.
     */
    _removeRow(index) {
        // The leaves are read before the subtree is destroyed: a nested collection answers what its
        // rows are, and a destroyed manager answers nothing — which used to leave its fields behind.
        const leaves = this._leafPaths(`${this._deps.path}.${index}`, this._deps.item);
        this._destroyNestedUnder(`${this._deps.path}.${index}`);
        for (const path of leaves) {
            // Ownership goes first: this is the row ending, which is the one thing that may take the
            // field with it.
            this._deps.engine.disownField(path);
        }
    }
    _leafPaths(fullPath, rowNode) {
        // Either kind of collection inside the row answers for its own subtree; a manager that is not
        // there yet has no leaves, which is what an undeclared row means.
        if (rowNode.kind === "record" || rowNode.kind === "array") {
            return this._nested.get(fullPath)?.leafPathsNow() ?? [];
        }
        if (rowNode.kind === "field")
            return [fullPath];
        return Object.entries(rowNode.children).flatMap(([key, child]) => this._leafPaths(`${fullPath}.${key}`, child));
    }
    _readNode(fullPath, rowNode) {
        if (rowNode.kind === "record" || rowNode.kind === "array") {
            const nested = this._nested.get(fullPath);
            if (nested)
                return nested.getValues();
            // The empty shape its kind has, so a collection nobody has declared into still reads as one.
            return rowNode.kind === "array" ? [] : {};
        }
        if (rowNode.kind === "field") {
            // `peekField`, not `getField`: this is a read, and the creating door would bring a field
            // into being as a side effect of being asked what the collection holds. The record manager
            // has always read through the door that creates nothing, and the host contract states the
            // difference in as many words.
            const ref = this._deps.engine.peekField(fullPath);
            return ref ? ref().value() : null;
        }
        const out = {};
        for (const [key, child] of Object.entries(rowNode.children)) {
            out[key] = this._readNode(`${fullPath}.${key}`, child);
        }
        return out;
    }
    /** The row index a path names under this collection, or `null` when it names no row of it. */
    _indexUnder(name) {
        const prefix = `${this._deps.path}.`;
        if (!name.startsWith(prefix))
            return null;
        const segment = name.slice(prefix.length).split(".")[0] ?? "";
        const index = Number(segment);
        return Number.isInteger(index) && index >= 0 && String(index) === segment ? index : null;
    }
    /** The rows the form holds under this collection, asked of the form rather than filtered out of it. */
    _declaredIndices() {
        const engine = this._deps.engine;
        if (!engine.childSegmentsUnder)
            return this._presentIndices(engine.fieldNames());
        const out = new Set();
        for (const segment of engine.childSegmentsUnder(this._deps.path)) {
            const index = Number(segment);
            if (Number.isInteger(index) && index >= 0 && String(index) === segment)
                out.add(index);
        }
        return out;
    }
    _presentIndices(names) {
        const prefix = `${this._deps.path}.`;
        const out = new Set();
        for (const name of names) {
            if (!name.startsWith(prefix))
                continue;
            const seg = name.slice(prefix.length).split(".")[0] ?? "";
            const n = Number(seg);
            if (Number.isInteger(n) && n >= 0 && String(n) === seg)
                out.add(n);
        }
        return out;
    }
    /**
     * Registers validators for rows that appeared via a raw flat write (draft/undo/redo)
     * and removes rows that disappeared via undo across a structural boundary.
     */
    _absorb(present) {
        const count = this._rowCountSig();
        let maxIndex = -1;
        // Register new rows that appeared (e.g., from draft restore or undo of a delete). The count is
        // raised first for the same reason as in `_rebuild`: the gate reads it.
        for (const idx of present)
            if (idx > maxIndex)
                maxIndex = idx;
        if (maxIndex + 1 > count)
            this._rowCountSig.set(maxIndex + 1);
        for (const idx of present) {
            if (idx >= count) {
                const value = this._readNode(`${this._deps.path}.${idx}`, this._deps.item);
                this._registerNode(`${this._deps.path}.${idx}`, this._deps.item, value, `${this._deps.path}.${idx}`, this._deps.sections ?? []);
            }
        }
        // Clean up rows that disappeared (e.g., undo of a push leaves a stale field)
        let removedAny = false;
        for (const idx of this._lastPresentIndices) {
            if (!present.has(idx)) {
                this._removeRow(idx);
                removedAny = true;
            }
        }
        const grown = maxIndex + 1;
        if (grown > count)
            this._rowCountSig.set(grown);
        if (removedAny) {
            this._rowCountSig.set(Math.max(grown, 0));
            this._deps.engine.refreshPathGate(this._deps.path);
        }
        // Update tracking for next reconciliation
        this._lastPresentIndices = new Set(present);
    }
}
